import { cn } from '@/lib/utils'
import { DriverActivityType } from '@/modules/activities/consts'
import { useActivityColor } from '@/utils'
import { useTranslate } from '@ur-solutions/react-hooks'

export interface WorkBar {
  key: string
  label: string
  title: string
  driveHours: number
  workHours: number
  availableHours: number
}

const SEGMENTS = [
  { field: 'driveHours', activityType: DriverActivityType.DRIVE },
  { field: 'workHours', activityType: DriverActivityType.WORK },
  { field: 'availableHours', activityType: DriverActivityType.AVAILABLE },
] as const

export const WorkLegend: React.FC = () => {
  const translations = useTranslate({
    driving: 'common.driving',
    otherWork: 'common.other-work',
    availability: 'common.availability',
  })
  const getActivityColor = useActivityColor()
  const labels = [
    translations.driving,
    translations.otherWork,
    translations.availability,
  ]

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-600">
      {SEGMENTS.map((segment, index) => (
        <span key={segment.field} className="inline-flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: getActivityColor(segment) }}
          />
          {labels[index]}
        </span>
      ))}
    </div>
  )
}

export const WorkBars: React.FC<{
  bars: WorkBar[]
  selected?: string | null
  onSelect?(key: string): void
  height?: number
  /** Show every n-th label so dense charts stay readable. */
  labelEvery?: number
}> = ({ bars, selected, onSelect, height = 160, labelEvery = 1 }) => {
  const getActivityColor = useActivityColor()
  const top = Math.max(
    1,
    ...bars.map(bar => bar.driveHours + bar.workHours + bar.availableHours)
  )

  return (
    <div
      className="grid items-end gap-[3px]"
      style={{ gridTemplateColumns: `repeat(${bars.length}, minmax(0, 1fr))` }}
    >
      {bars.map((bar, index) => {
        const total = bar.driveHours + bar.workHours + bar.availableHours
        const isSelected = bar.key === selected

        return (
          <button
            key={bar.key}
            type="button"
            title={bar.title}
            disabled={!onSelect}
            onClick={() => onSelect?.(bar.key)}
            className="group flex min-w-0 flex-col items-stretch gap-1 disabled:cursor-default"
          >
            <div
              className={cn(
                'flex flex-col-reverse justify-start overflow-hidden rounded-t-sm',
                onSelect && 'group-hover:opacity-80',
                selected && !isSelected && 'opacity-50',
                isSelected && 'ring-2 ring-stone-900 ring-offset-1'
              )}
              style={{ height }}
            >
              <div
                className="flex flex-col-reverse"
                style={{ height: `${(total / top) * 100}%` }}
              >
                {SEGMENTS.map(segment => (
                  <div
                    key={segment.field}
                    style={{
                      backgroundColor: getActivityColor(segment),
                      height: total
                        ? `${(bar[segment.field] / total) * 100}%`
                        : 0,
                    }}
                  />
                ))}
              </div>
            </div>
            <span
              className={cn(
                'whitespace-nowrap text-center text-[10px] tabular-nums text-stone-500',
                isSelected && 'font-semibold text-stone-900'
              )}
            >
              {index % labelEvery === 0 || isSelected ? bar.label : '\u00a0'}
            </span>
          </button>
        )
      })}
    </div>
  )
}
